import React, { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential,
} from "firebase/auth";
import { auth } from "../config/firebase";

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  function signup(email, password, displayName) {
    return createUserWithEmailAndPassword(auth, email, password).then(
      (result) =>
        updateProfile(result.user, { displayName }).then(() => result)
    );
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function loginWithGoogle() {
    // signInWithPopup is blocked inside Chrome extensions.
    // Use chrome.identity.launchWebAuthFlow for native OAuth in extensions.
    if (process.env.REACT_APP_EXTENSION === "true") {
      /* global chrome */
      const clientId = "1075843762063-4fp1e6h2v7nleanb0p5qr1l29rbjcjhr.apps.googleusercontent.com";
      const redirectUrl = chrome.identity.getRedirectURL();
      const authUrl =
        "https://accounts.google.com/o/oauth2/v2/auth" +
        `?client_id=${clientId}` +
        `&redirect_uri=${encodeURIComponent(redirectUrl)}` +
        "&response_type=token" +
        "&scope=openid%20email%20profile";

      return new Promise((resolve, reject) => {
        chrome.identity.launchWebAuthFlow(
          { url: authUrl, interactive: true },
          async (callbackUrl) => {
            if (chrome.runtime.lastError || !callbackUrl) {
              reject(new Error(chrome.runtime.lastError?.message || "Google login cancelled"));
              return;
            }
            try {
              const params = new URLSearchParams(new URL(callbackUrl.replace("#", "?")).search);
              const accessToken = params.get("access_token");
              const credential = GoogleAuthProvider.credential(null, accessToken);
              const userResult = await signInWithCredential(auth, credential);
              resolve(userResult);
            } catch (err) {
              reject(err);
            }
          }
        );
      });
    }
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  }

  function logout() {
    return signOut(auth);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loading,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
